package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceArtefactsResponse
import ai.jessie.web.members.deniesAccess
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch
import org.w3c.dom.url.URL

// A received PDF as a page-local object URL; never shared across invoices or organisations.
data class InvoiceArtefactFile(val fileName: String, val url: String)

data class InvoiceArtefactUiState(
    val artefacts: InvoiceArtefactsResponse? = null,
    val invoicePdf: InvoiceArtefactFile? = null,
    val workReport: InvoiceArtefactFile? = null,
    val confirming: Boolean = false,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val notice: String? = null,
)

data class InvoiceArtefactActions(
    val refresh: () -> Unit,
    val invoicePdf: () -> Unit,
    val requestWorkReport: () -> Unit,
    val confirmWorkReport: () -> Unit,
    val cancelWorkReport: () -> Unit,
    val workReport: () -> Unit,
)

/**
 * UC-015 artefacts of one issued invoice. Single-flight; a disposed page scope cannot publish late
 * answers, so a change of organisation or invoice never shows another tenant's file. A PDF is
 * offered only after its complete body arrived (A8). A tenant denial clears the artefacts here and
 * the invoice itself through [accessLost] (A7).
 */
class InvoiceArtefactState(
    private val port: InvoiceArtefactPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val invoiceId: String,
    private val accessLost: (OnboardingProblem) -> Unit,
) {
    var state by mutableStateOf(InvoiceArtefactUiState())
        private set

    val actions =
        InvoiceArtefactActions(
            refresh = ::refresh,
            invoicePdf = ::invoicePdf,
            // Step 6: the report is only requested after confirmation.
            requestWorkReport = {
                if (!state.busy && state.artefacts?.workReport == REPORT_AVAILABLE)
                    state = state.copy(confirming = true, error = null, notice = null)
            },
            confirmWorkReport = ::confirmWorkReport,
            // A4: cancelling sends nothing; the invoice PDF stays as it is.
            cancelWorkReport = {
                if (!state.busy && state.confirming)
                    state =
                        state.copy(
                            confirming = false,
                            notice =
                                "Kein Arbeitsrapport angefordert. Das Rechnungs-PDF bleibt " +
                                    "unverändert.",
                        )
            },
            workReport = ::workReport,
        )

    fun refresh() {
        request(generating = false) {
            val artefacts = port.artefacts(organisationId, invoiceId)
            currentCoroutineContext().ensureActive()
            state = state.copy(artefacts = artefacts)
        }
    }

    // Step 4: the server returns the canonical PDF or generates and links it first.
    private fun invoicePdf() {
        val artefacts = state.artefacts ?: return
        if (artefacts.invoicePdf == PDF_SNAPSHOT_UNAVAILABLE) return
        request {
            val file = port.invoicePdf(organisationId, invoiceId)
            currentCoroutineContext().ensureActive()
            state =
                state.copy(
                    artefacts = state.artefacts?.copy(invoicePdf = ARTEFACT_LINKED),
                    invoicePdf = replaced(state.invoicePdf, file),
                )
        }
    }

    // Step 7: the confirmed request links the report, then its complete PDF is offered.
    private fun confirmWorkReport() {
        if (!state.confirming) return
        request {
            val artefacts = port.requestWorkReport(organisationId, invoiceId)
            currentCoroutineContext().ensureActive()
            state = state.copy(artefacts = artefacts, confirming = false)
            receiveWorkReport()
        }
    }

    private fun workReport() {
        if (state.artefacts?.workReport != ARTEFACT_LINKED) return
        request { receiveWorkReport() }
    }

    private suspend fun receiveWorkReport() {
        val file = port.workReport(organisationId, invoiceId)
        currentCoroutineContext().ensureActive()
        state = state.copy(workReport = replaced(state.workReport, file))
    }

    // Releases every object URL when the page, invoice or organisation changes.
    fun dispose() {
        state.invoicePdf?.let { URL.revokeObjectURL(it.url) }
        state.workReport?.let { URL.revokeObjectURL(it.url) }
    }

    private fun replaced(previous: InvoiceArtefactFile?, file: InvoicePdf): InvoiceArtefactFile {
        previous?.let { URL.revokeObjectURL(it.url) }
        return InvoiceArtefactFile(file.fileName, URL.createObjectURL(file.content))
    }

    private fun request(generating: Boolean = true, block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: InvoiceRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure, generating)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // A8: nothing unconfirmed is offered; earlier complete files stay.
                state =
                    state.copy(
                        confirming = false,
                        error = if (generating) notCompletedProblem() else unavailableProblem(),
                    )
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private fun rejected(failure: InvoiceRequestFailure, generating: Boolean) {
        val code = failure.detail.code
        val artefacts = state.artefacts
        state =
            when {
                // A7: no invoice, invoice-PDF or work-report detail stays.
                OnboardingRequestFailure(failure.status, code).deniesAccess() -> {
                    dispose()
                    accessLost(failure.invoiceProblem(saving = false))
                    InvoiceArtefactUiState(busy = true)
                }
                // A5: Draft or cancelled before issuance.
                code == "INVOICE_NOT_ISSUED" -> {
                    dispose()
                    InvoiceArtefactUiState(busy = true, error = notIssuedProblem())
                }
                // A6: nothing is generated or replaced.
                code == PDF_SNAPSHOT_UNAVAILABLE ->
                    state.copy(
                        artefacts = artefacts?.copy(invoicePdf = PDF_SNAPSHOT_UNAVAILABLE),
                        confirming = false,
                        error = snapshotUnavailableProblem(),
                    )
                // A3: the invoice PDF stays offered.
                code == "WORK_REPORT_UNAVAILABLE" ->
                    state.copy(
                        artefacts = artefacts?.copy(workReport = REPORT_UNAVAILABLE),
                        confirming = false,
                        error = workReportUnavailableProblem(),
                    )
                code == "WORK_REPORT_NOT_REQUESTED" ->
                    state.copy(
                        artefacts = artefacts?.copy(workReport = REPORT_AVAILABLE),
                        error =
                            OnboardingProblem(
                                "Der Arbeitsrapport wurde noch nicht angefordert. Bitte fordere " +
                                    "ihn zuerst an."
                            ),
                    )
                // A8.
                generating && failure.status >= HTTP_SERVER_ERROR ->
                    state.copy(confirming = false, error = notCompletedProblem())
                else -> state.copy(confirming = false, error = failure.invoiceProblem(false))
            }
    }
}

internal fun notIssuedProblem() =
    OnboardingProblem("Für diese Rechnung ist kein ausgestelltes Rechnungs-PDF verfügbar.")

internal fun snapshotUnavailableProblem() =
    OnboardingProblem(
        "Das Rechnungs-PDF kann nicht erstellt werden, weil der festgehaltene Rechnungsstand " +
            "unvollständig ist. Es wurde kein PDF erstellt oder ersetzt."
    )

internal fun workReportUnavailableProblem() =
    OnboardingProblem(
        "Der Arbeitsrapport ist nicht verfügbar, weil die festgehaltene Arbeit unvollständig ist. " +
            "Das Rechnungs-PDF bleibt verfügbar."
    )

internal fun notCompletedProblem() =
    OnboardingProblem(
        "Die PDF-Erstellung wurde nicht abgeschlossen. Es wird kein unvollständiges PDF " +
            "angeboten; bereits gespeicherte PDFs bleiben erhalten. Bitte versuche es erneut."
    )
